import React from 'react';
import { Tooltip } from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEyeSlash,
    faEye,
    faDollarSign,
    faSearch,
    faSortDown,
    faTimes,
    faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';

export const PriceToggle = ({ showPrices, onToggle }) => (
    <button
        onClick={onToggle}
        className={`flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-200 font-medium shadow-md hover:shadow-lg border ${
            showPrices
                ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-green-500 hover:scale-105'
                : 'bg-gradient-to-r from-white to-gray-50 hover:from-gray-50 hover:to-gray-100 text-gray-700 border-gray-300 hover:scale-105'
        }`}
        data-tooltip-id="price-toggle-tooltip"
    >
        <FontAwesomeIcon
            icon={showPrices ? faEyeSlash : faEye}
            className={`text-sm ${showPrices ? 'text-green-100' : 'text-gray-500'}`}
        />
        <FontAwesomeIcon
            icon={faDollarSign}
            className={`text-lg ${showPrices ? 'text-green-200' : 'text-green-600'}`}
        />
        <span className="font-semibold">{showPrices ? 'Hide' : 'Show'} Prices</span>
        <Tooltip id="price-toggle-tooltip" place="bottom" className="bg-gray-800 text-white text-xs py-2 px-3 rounded-lg shadow-xl">
            <span>{showPrices ? 'Hide' : 'Show'} set market values</span>
        </Tooltip>
    </button>
);

const SortButton = ({ option, label, currentOption, onSort, getSortIcon, colSpan2 }) => (
    <button
        onClick={() => onSort(option)}
        className={`px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md ${colSpan2 ? 'col-span-2' : ''} ${
            currentOption === option
                ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 hover:scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
        }`}
    >
        {label}
        {getSortIcon(option) && (
            <FontAwesomeIcon icon={getSortIcon(option)} className="text-xs" />
        )}
    </button>
);

const FilterPanel = ({
    searchQuery,
    setSearchQuery,
    filterOptions,
    setFilterOptions,
    sortOption,
    isListView,
    isWishlist,
    filteredSets,
    sets,
    handleSortChange,
    getSortIcon,
    resetFilters,
    getDecadeOptions,
}) => {
    const hasActiveFilters = searchQuery || filterOptions.complete !== 'all' || filterOptions.sealed !== 'all' || filterOptions.decade !== 'all';

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 p-6 sm:p-8 mb-8 transition-all duration-300">
            <div className="flex flex-col lg:flex-row justify-between gap-8">
                {/* Search & Filters */}
                <div className="flex-grow">
                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-600 text-sm" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800">Search &amp; Filter</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                        {/* Search */}
                        <div className="relative">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Sets</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search by name, number, or theme..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 hover:shadow-md"
                                />
                            </div>
                        </div>

                        {!isWishlist && (
                            <>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Completion Status</label>
                                    <select
                                        value={filterOptions.complete}
                                        onChange={e => setFilterOptions(prev => ({ ...prev, complete: e.target.value }))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 hover:shadow-md font-medium"
                                    >
                                        <option value="all">All Sets</option>
                                        <option value="complete">Only Complete Sets</option>
                                        <option value="incomplete">Has Incomplete Sets</option>
                                        <option value="mixed">Mixed (Some Complete, Some Not)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Package Status</label>
                                    <select
                                        value={filterOptions.sealed}
                                        onChange={e => setFilterOptions(prev => ({ ...prev, sealed: e.target.value }))}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 hover:shadow-md font-medium"
                                    >
                                        <option value="all">All Sets</option>
                                        <option value="sealed">Only Sealed Sets</option>
                                        <option value="opened">Has Opened Sets</option>
                                        <option value="mixed">Mixed (Some Sealed, Some Opened)</option>
                                    </select>
                                </div>
                            </>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Release Decade</label>
                            <select
                                value={filterOptions.decade}
                                onChange={e => setFilterOptions(prev => ({ ...prev, decade: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-200 hover:shadow-md font-medium"
                            >
                                <option value="all">All Decades</option>
                                {getDecadeOptions().map(decade => (
                                    <option key={decade} value={decade}>{decade}s</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Sort (hidden in list view) */}
                {!isListView && (
                    <div className="border-t lg:border-t-0 lg:border-l border-gray-300 pt-6 lg:pt-0 lg:pl-8">
                        <div className="flex items-center mb-6">
                            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                                <FontAwesomeIcon icon={faSortDown} className="text-gray-600 text-sm" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800">Sort By</h3>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {['theme', 'name', 'year', 'pieces'].map(opt => (
                                <SortButton
                                    key={opt}
                                    option={opt}
                                    label={opt.charAt(0).toUpperCase() + opt.slice(1)}
                                    currentOption={sortOption}
                                    onSort={handleSortChange}
                                    getSortIcon={getSortIcon}
                                />
                            ))}
                            <SortButton
                                option="value"
                                label="Market Value"
                                currentOption={sortOption}
                                onSort={handleSortChange}
                                getSortIcon={getSortIcon}
                                colSpan2
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Active filter summary */}
            {hasActiveFilters && (
                <div className="mt-8 pt-6 border-t border-gray-300">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center shadow-sm">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-white text-sm" />
                            </div>
                            <div>
                                <span className="text-lg font-bold text-gray-800">{filteredSets.length}</span>
                                <span className="text-gray-600 ml-1">set{filteredSets.length === 1 ? '' : 's'} found</span>
                            </div>
                        </div>
                        <button
                            onClick={resetFilters}
                            className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105"
                        >
                            <FontAwesomeIcon icon={faTimes} className="mr-2" />
                            Clear All Filters
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterPanel;
